import React, { useState } from 'react';
import { motion } from 'framer-motion';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Auth = ({ showToast }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);

  const checkPaidSubscription = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      if (error && error.code !== 'PGRST116') { // Not "No rows returned" error
        throw error;
      }

      return data; // Returns subscription data if active, null if none
    } catch (error) {
      console.error('Error checking subscription:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp) {
        // Sign up with email and password
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
        });
        
        if (authError) throw authError;

        // If sign up successful and we have user data, update profile with names
        if (authData.user) {
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              first_name: firstName,
              last_name: lastName
            })
            .eq('user_id', authData.user.id);

          if (profileError) throw profileError;
        }
        
        showToast('Account created! Please check your email to verify.');
      } else {
        // Sign in flow
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (authError) {
          // Check if error is due to invalid credentials
          if (authError.message.includes('Invalid login credentials')) {
            // Check if user exists at all
            const { data: { users }, error: userCheckError } = await supabase.auth.admin.listUsers();
            const userExists = users?.some(user => user.email === email);
            
            if (userCheckError) {
              throw authError; // Fall back to original error if admin check fails
            }
            
            if (!userExists) {
              showToast('No account found with this email address. Please sign up first.', 'error');
            } else {
              // User exists but invalid password - check if they're a paid member
              try {
                // Try to get user by email (this might not work due to RLS, but let's try)
                const { data: profileData } = await supabase
                  .from('profiles')
                  .select('user_id')
                  .eq('email', email)
                  .single();
                
                if (profileData) {
                  // User exists, check if they have active subscription
                  const hasSubscription = await checkPaidSubscription(profileData.user_id);
                  if (!hasSubscription) {
                    showToast('This account exists but does not have an active subscription. Please complete your payment first.', 'error');
                  } else {
                    showToast('Invalid password. Please try again.', 'error');
                  }
                } else {
                  showToast('Invalid login credentials. Please check your email and password.', 'error');
                }
              } catch (checkError) {
                // If we can't check subscription details, show generic message
                showToast('Invalid login credentials. Please check your email and password.', 'error');
              }
            }
          } else {
            throw authError;
          }
          return; // Exit early on auth error
        }
        
        // If sign in successful, check for active subscription
        if (authData.user) {
          const hasSubscription = await checkPaidSubscription(authData.user.id);
          if (!hasSubscription) {
            showToast('Your account exists but you do not have an active subscription. Please complete your payment to access the tools.', 'error');
            // Sign them out since they don't have access
            await supabase.auth.signOut();
            return;
          }
          
          showToast('Successfully signed in!');
        }
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/30 backdrop-blur-sm">
          <div className="text-center mb-8">
            <SafeIcon icon={FiIcons.FiShield} className="w-12 h-12 mx-auto mb-4 text-blue-400" />
            <h1 className="text-3xl font-bold text-white mb-2">Sneaky Redirects</h1>
            <p className="text-slate-400">
              {isSignUp ? 'Create your account' : 'Sign in to your account'}
            </p>
            {!isSignUp && (
              <p className="text-xs text-slate-500 mt-2">
                Access requires an active subscription
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {isSignUp && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your password"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-medium rounded-lg transition duration-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                isSignUp ? 'Sign Up' : 'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-blue-400 hover:text-blue-300 text-sm transition duration-200"
            >
              {isSignUp 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Sign up"
              }
            </button>
          </div>

          {!isSignUp && (
            <div className="mt-4 text-center">
              <p className="text-xs text-slate-500">
                New users must sign up and complete payment to access the tools
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;